import { BaseResponse } from "../../db/response";
import CryptoJS from 'crypto-js';

const normalizeBaseResponse = (response: any): BaseResponse => {
  // Handle plain string responses (e.g., "Invalid credentials provided.")
  if (typeof response === 'string') {
    return {
      status: "FAILURE",
      statusinfo: {
        code: "ERROR",
        message: response,
      },
    };
  }
  
  // Handle swagger allOf structure: StatusResponse with data and errors
  // Response structure: { status, statusinfo, data?, errors? }
  
  // If response already has the expected structure (flat status, not nested)
  if (response?.status && (response.status === "SUCCESS" || response.status === "FAILURE") && response?.statusinfo) {
    return {
      status: response.status,
      statusinfo: response.statusinfo,
      data: response.data,
      errors: response.errors,
    };
  }

  // Handle nested status structure (legacy format)
  if (response?.status?.status && response?.status?.statusinfo) {
    return {
      status: response.status.status,
      statusinfo: response.status.statusinfo,
      data: response.status.data || response.data,
      errors: response.errors,
    };
  }

  // Check for simple error format: {"error": "Unauthorized"}
  if (response?.error && typeof response.error === 'string') {
    return {
      status: "FAILURE",
      statusinfo: {
        code: "ERROR",
        message: response.error,
      },
      errors: { error: response.error },
    };
  }

  // Check for errors in swagger format (errors object/array)
  if (response?.errors) {
    let errorMessage = "An error occurred";
    let errorCode = "UNKNOWN_ERROR";
    
    if (Array.isArray(response.errors) && response.errors.length > 0) {
      const firstError = response.errors[0];
      errorMessage = firstError.message || errorMessage;
      errorCode = firstError.code || errorCode;
    } else if (typeof response.errors === 'object' && response.errors.message) {
      errorMessage = response.errors.message;
      errorCode = response.errors.code || errorCode;
    }
    
    return {
      status: "FAILURE",
      statusinfo: {
        code: errorCode,
        message: errorMessage,
      },
      errors: response.errors,
    };
  }

  // Check for errors array in result (legacy format)
  if (
    Array.isArray(response?.result?.errors) &&
    response.result.errors.length > 0
  ) {
    const firstError = response.result.errors[0];
    return {
      status: "FAILURE",
      statusinfo: {
        code: firstError.code || "UNKNOWN_ERROR",
        message: firstError.message || "An error occurred.",
      },
      errors: response.result.errors,
    };
  }

  // Check if status is explicitly FAILURE (flat or nested)
  if (response?.status === "FAILURE" || response?.status?.status === "FAILURE") {
    return {
      status: "FAILURE",
      statusinfo: {
        code: response?.statusinfo?.code || response?.status?.statusinfo?.code || response?.code || "FAILURE",
        message: response?.statusinfo?.message || response?.status?.statusinfo?.message || response?.message || "Operation failed.",
      },
      errors: response.errors,
    };
  }

  // Check if status is SUCCESS (flat or nested)
  if (response?.status === "SUCCESS" || response?.status?.status === "SUCCESS") {
    return {
      status: "SUCCESS",
      statusinfo: {
        code: response?.statusinfo?.code || response?.status?.statusinfo?.code || "SUCCESS",
        message: response?.statusinfo?.message || response?.status?.statusinfo?.message || "Operation completed successfully.",
      },
      data: response?.data || response?.status?.data || null,
      errors: response.errors,
    };
  }

  // For unknown structures, be conservative and return FAILURE
  // This prevents false positives that would navigate to OTP screen
  return {
    status: "FAILURE",
    statusinfo: {
      code: "UNKNOWN_RESPONSE",
      message: response?.message || "Unexpected response format.",
    },
  };
};

// Use Vite env variable for frontend builds
const SECRET_KEY = import.meta.env.VITE_SECRET_KEY || 'default-secret-key'; // Use environment variable or fallback to a default key

// Encrypt data
export const encryptData = (data: any): string => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
};

// Decrypt data
export const decryptData = (cipherText: string): any => {
  const bytes = CryptoJS.AES.decrypt(cipherText, SECRET_KEY);
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedData);
};
export default normalizeBaseResponse;
